import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { getJson } from "./getJson.js";
import type { SignalDetailResponse, SignalsResponse } from "./queries.js";
import { feedLimit, feedSessions, queryKeys, withParams } from "./queries.js";
import type { SignalsSearch } from "./search-params.js";

/**
 * The two reads the signals pages make.
 *
 * Split out of `queries.ts`, which had grown past the 500-line cap holding the
 * response types of every route plus the hooks that fetch them. These two are
 * the feed's own, and they are the pair a filter change has to travel through:
 * `signalsRequestPath` carries it to the server and `queryKeys.signals` keys
 * the cache on it. Missing either one is a filter that changes the URL and
 * nothing else — see `queries.test.ts`, which drives both off the same list.
 */

/**
 * The URL one feed search asks for, filters and all.
 *
 * Its own function so a test can read it. Every filter has to appear here as
 * well as in `queryKeys.signals`, and the Size filter shipped in neither: the
 * control wrote the URL, the route honoured the parameter, and the page still
 * showed every row because the request never carried it.
 */
export function signalsRequestPath(search: SignalsSearch): string {
  return withParams("/api/signals", {
    family: search.family,
    minSeverity: search.minSeverity,
    symbol: search.symbol,
    signalType: search.signalType,
    marketCap: search.marketCap,
    side: search.side,
    acknowledged: search.acknowledged,
    // Zero is "every session", and the route says that with an absent
    // parameter. `withParams` drops undefined, so this is the whole
    // translation.
    sessions: feedSessions(search) === 0 ? undefined : String(feedSessions(search)),
    limit: String(feedLimit(search)),
  });
}

export function useSignals(search: SignalsSearch) {
  return useQuery({
    queryKey: queryKeys.signals(search),
    queryFn: () => getJson<SignalsResponse>(signalsRequestPath(search)),
    // The feed is the one surface where a stale read is a missed alert.
    staleTime: 30_000,
    // Against the client-wide default, which is off. Nothing else refreshes an
    // open feed in place: there is no polling interval, and acknowledging a
    // signal patches the cache rather than refetching it. Coming back to the
    // tab is when a reader expects to see what fired while they were away, and
    // the 30-second staleTime keeps a flurry of tab switches to one request.
    refetchOnWindowFocus: true,
    // Widening the window or loading another page changes the key, and
    // without this the table unmounts to "Loading the feed…" and remounts
    // every row — the visible half of the freeze a reader reported. Keeping
    // the previous page on screen makes it a background refetch instead.
    placeholderData: keepPreviousData,
  });
}

/**
 * One signal, with the context its detail view draws.
 *
 * Five minutes, not the feed's thirty seconds: a signal that has already fired
 * does not change.
 */
export function useSignal(id: string) {
  return useQuery({
    queryKey: queryKeys.signal(id),
    queryFn: () => getJson<SignalDetailResponse>(`/api/signals/${encodeURIComponent(id)}`),
    enabled: id !== "",
    staleTime: 5 * 60_000,
  });
}
